/**
 * Store для платформенных in-app уведомлений (отдельный от toast NotificationManager).
 *
 * Толстый клиент: держит локальный кеш ленты + счётчика непрочитанных,
 * умеет обновляться через polling и WS push.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";

import apiService, { type NotificationItem } from "@/services/api";

interface InAppNotificationsState {
  items: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;

  reload: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  // Применить событие из WS-канала.
  applyIncoming: (item: NotificationItem) => void;
}

export const useInAppNotificationsStore = create<InAppNotificationsState>()(
  devtools(
    (set, get) => ({
      items: [],
      unreadCount: 0,
      isLoading: false,
      isLoaded: false,
      error: null,

      reload: async () => {
        try {
          set({ isLoading: true, error: null });
          const response = await apiService.listNotifications({ limit: 20 });
          set({
            items: response.items,
            unreadCount: response.total_unread,
            isLoaded: true,
            isLoading: false,
          });
        } catch (err) {
          console.error("Failed to load notifications:", err);
          set({ error: "Не удалось загрузить уведомления", isLoading: false });
        }
      },

      refreshUnreadCount: async () => {
        try {
          const response = await apiService.getNotificationsUnreadCount();
          set({ unreadCount: response.count });
        } catch (err) {
          console.error("Failed to refresh unread count:", err);
        }
      },

      markRead: async (id) => {
        // Оптимистично обновляем UI.
        const prev = get().items;
        const wasUnread = prev.some((it) => it.id === id && !it.is_read);
        set((state) => ({
          items: state.items.map((it) => (it.id === id ? { ...it, is_read: true } : it)),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        }));
        try {
          await apiService.markNotificationRead(id);
        } catch (err) {
          console.error("Failed to mark read:", err);
          // Возврат к предыдущему состоянию при ошибке.
          set({ items: prev });
          await get().refreshUnreadCount();
        }
      },

      markAllRead: async () => {
        const prev = get().items;
        set((state) => ({
          items: state.items.map((it) => ({ ...it, is_read: true })),
          unreadCount: 0,
        }));
        try {
          await apiService.markAllNotificationsRead();
        } catch (err) {
          console.error("Failed to mark all read:", err);
          set({ items: prev });
          await get().refreshUnreadCount();
        }
      },

      applyIncoming: (item) => {
        set((state) => {
          // Защита от дублей при гонке WS+polling.
          if (state.items.some((it) => it.id === item.id)) {
            return state;
          }
          return {
            items: [item, ...state.items].slice(0, 50),
            unreadCount: state.unreadCount + (item.is_read ? 0 : 1),
          };
        });
      },
    }),
    { name: "InAppNotificationsStore" },
  ),
);
